import {
	Box,
	TextField,
	LoadingButton,
	Form,
	Field,
	Typography,
	PasswordField,
	PasswordRulesChecker,
	TelephoneField,
	FormOnSubmitReturn,
	FormStack,
	Divider,
	Link,
} from 'components/common';
import { useTranslation } from 'hooks';
import { useCallback, useMemo } from 'react';
import { sleep } from 'utils/delay';
import { Trans } from 'react-i18next';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { name, phoneNumber } from 'utils/validation';
import { PhoneNumberValue } from 'core/types';
import config from 'config';
import { buttonSectionStyle } from './styles';

export type RequestToJoinFormSubmittedValues = {
	firstName: string;
	lastName: string;
	phone: string;
	notes: string;
	password: string;
	email: string;
};

type RequestToJoinFormValues = Omit<
	RequestToJoinFormSubmittedValues,
	'phone'
> & {
	phone?: PhoneNumberValue;
};

type RequestToJoinFormProps = {
	isLoggedIn: boolean;
	hasAccount: boolean;
	initialValues?: Partial<RequestToJoinFormSubmittedValues>;
	onSubmit?: (values: RequestToJoinFormSubmittedValues) => FormOnSubmitReturn;
	submitText?: string;
	submittingText?: string;
};

export const RequestToJoinForm = (props: RequestToJoinFormProps) => {
	const {
		isLoggedIn,
		hasAccount,
		initialValues,
		onSubmit,
		submitText,
		submittingText,
	} = props;
	const { t } = useTranslation();

	const handleOnSubmit = useCallback(
		async ({ phone, ...values }: RequestToJoinFormValues) => {
			await sleep(1);

			const result = onSubmit?.({
				...values,
				phone: phone ? `+${phone.dialCode}${phone.number}` : '',
			});

			return Promise.resolve(result).catch(error => error);
		},
		[onSubmit]
	);

	const validationSchema = useMemo(
		() => createValidationSchema(t, !isLoggedIn && !hasAccount),
		[isLoggedIn, hasAccount]
	);

	return (
		<Form<RequestToJoinFormValues>
			onSubmit={handleOnSubmit}
			validationSchema={validationSchema}
			subscription={{ submitting: true }}
			initialValues={initialValues as Partial<RequestToJoinFormValues>}
		>
			{({ handleSubmit, submitting }) => (
				<form onSubmit={handleSubmit} noValidate>
					<FormStack>
						{!hasAccount && (
							<>
								<Box>
									<Field
										label={t('common-label.first-name')}
										name='firstName'
										component={TextField}
										type='text'
										fullWidth
										required
									/>
								</Box>
								<Box>
									<Field
										label={t('common-label.last-name')}
										name='lastName'
										component={TextField}
										type='text'
										fullWidth
										required
									/>
								</Box>
								<Box>
									<Field
										label={t('common-label.phone')}
										name='phone'
										component={TelephoneField}
										type='text'
										placeholder='(201) 555-0123'
										fullWidth
										required
									/>
								</Box>
							</>
						)}
						<Box>
							<Field
								label={t('common-label.email')}
								name='email'
								component={(props: { value: string }) => (
									<Typography variant='body1'>
										<Trans
											i18nKey='request-to-join.email'
											values={{
												email: props.value,
											}}
											components={{
												bold: <b />,
											}}
										/>
									</Typography>
								)}
								required
							/>
						</Box>
						<Box>
							<Field
								label={t('request-to-join.information')}
								name='notes'
								component={TextField}
								placeholder={t(
									'request-to-join.information-placeholder'
								)}
								multiline
								rows={4}
								fullWidth
							/>
						</Box>
						{!isLoggedIn && (
							<Field
								label={t('common-label.password')}
								name='password'
								component={PasswordField}
								type='password'
								fullWidth
								required
							/>
						)}
						{!hasAccount && (
							<PasswordRulesChecker
								forField='password'
								usernameField='email'
								initialEmail={initialValues?.email || ''}
							/>
						)}
					</FormStack>
					<Divider mt={5} mb={2} />
					<FormStack>
						<Box>
							<Trans
								i18nKey='request-to-join.accept-message'
								components={{
									term: (
										<Link
											href={`${config.urls.wordPress}/terms`}
											target='_blank'
										/>
									),
								}}
							/>
						</Box>
						<Box sx={buttonSectionStyle}>
							<LoadingButton
								loading={submitting}
								loadingIndicator={
									submittingText || t('form.submitting')
								}
								size='small'
								type='submit'
							>
								{submitText || t('form.submit')}
							</LoadingButton>
						</Box>
					</FormStack>
				</form>
			)}
		</Form>
	);
};

function createValidationSchema(t: TFunction, verifyUserFields: boolean) {
	if (verifyUserFields) {
		return Yup.object().shape({
			firstName: name(t('form.invalid-name')).required(
				t('form.generic-required')
			),
			lastName: name(t('form.invalid-name')).required(
				t('form.generic-required')
			),
			email: Yup.string()
				.email(t('form.invalid-email'))
				.required(t('form.generic-required')),
			phone: phoneNumber(t('form.invalid-phone')).required(
				t('form.generic-required')
			),
		});
	}

	return Yup.object().shape({});
}
